import type { DashboardDateRange } from '@/components/dashboard/model';
import { dashboardRangeKey } from '../../../dashboard/hooks';
import { ReportQueryKind, useReportQueries } from '../../../reporting/reportExplorer/hooks';
import {
  buildIntegrationDashboardReportRequest,
  previousIntegrationDashboardRange,
} from './reportRequest';
import type { IntegrationDashboardQueryBundle } from './widgetRuntime';

export function useIntegrationDashboardQueries(
  integrationId: string,
  ranges: readonly DashboardDateRange[],
) {
  const uniqueRanges = new Map(ranges.map((range) => [dashboardRangeKey(range), range]));
  const descriptors = [...uniqueRanges.entries()].flatMap(([key, range]) => {
    const previousRange = previousIntegrationDashboardRange(range);
    return [
      {
        id: `${key}:current`,
        rangeKey: key,
        period: IntegrationDashboardPeriod.Current,
        request: buildIntegrationDashboardReportRequest(integrationId, range),
      },
      ...(previousRange
        ? [
            {
              id: `${key}:previous`,
              rangeKey: key,
              period: IntegrationDashboardPeriod.Previous,
              request: buildIntegrationDashboardReportRequest(integrationId, previousRange),
            },
          ]
        : []),
    ];
  });
  const queries = useReportQueries(
    descriptors.map(({ request }) => ({
      kind: ReportQueryKind.Chart,
      request,
      enabled: Boolean(integrationId),
    })),
  );
  const bundles = new Map<string, IntegrationDashboardQueryBundle>();
  descriptors.forEach((descriptor, index) => {
    const query = queries[index];
    if (!query) return;
    const existing = bundles.get(descriptor.rangeKey);
    if (descriptor.period === IntegrationDashboardPeriod.Current) {
      bundles.set(descriptor.rangeKey, {
        request: descriptor.request,
        current: query,
        previous: existing?.previous,
      });
    } else if (existing) {
      bundles.set(descriptor.rangeKey, { ...existing, previous: query });
    }
  });
  return bundles;
}

enum IntegrationDashboardPeriod {
  Current = 'CURRENT',
  Previous = 'PREVIOUS',
}
